import React, { useEffect, useState } from "react";
import Admin from "../layout/Admin";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getPrintErrors } from "../../actions/printError";
import { getPrinters } from "../../actions/printer";
import spinner from "../layout/spinner.gif";
import ResolvePrintError from "./ResolvePrintError";
import PrintErrorDetail from "./PrintErrorDetail";
import {
  errorCodeLabel,
  statusOf,
  STATUSES,
  money,
  dateText,
  phoneOf,
} from "./printErrorLabels";

const fieldClass =
  "block w-full h-10 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";
const selectClass = fieldClass + " pl-3 pr-10";
const dateClass = fieldClass + " min-w-0 px-3";
const labelClass =
  "block mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400";

const STATUS_FILTERS = [
  "pending",
  "refunded",
  "rejected",
  "resolved",
  "no_payment",
];

const COLUMNS = 7;

const DEFAULT_FILTERS = {
  status: "pending",
  printerId: "",
  startDate: "",
  endDate: "",
};

const isDefault = (filters) =>
  Object.keys(DEFAULT_FILTERS).every(
    (key) => filters[key] === DEFAULT_FILTERS[key]
  );

const PrintErrors = ({
  getPrintErrors,
  getPrinters,
  printError: { errors, loading },
  printers,
}) => {
  const [formData, setFormData] = useState(DEFAULT_FILTERS);
  const { status, printerId, startDate, endDate } = formData;
  const [selectedId, setSelectedId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // The row whose detail is open under it; one at a time
  const [expandedId, setExpandedId] = useState(null);
  // What the list on screen was loaded with, so a 409 reloads the same list
  const [loadedWith, setLoadedWith] = useState(formData);

  const load = (filters) => {
    setLoadedWith(filters);
    getPrintErrors(filters);
  };

  useEffect(() => {
    load(formData);
    // The kiosk filter needs the printer list; the Printer page may have loaded it already
    if (printers.length === 0) getPrinters();
    // Only the initial filters are loaded automatically; later ones go through Search
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!loading) load(formData);
  };

  const onReset = () => {
    setFormData(DEFAULT_FILTERS);
    load(DEFAULT_FILTERS);
  };
  const canReset = !isDefault(formData) || !isDefault(loadedWith);

  const openError = (item) => {
    setSelectedId(item._id);
    setShowModal(true);
  };
  const selected = errors.find((item) => item._id === selectedId);

  // The row holds what tells one error from another; the rest is in PrintErrorDetail
  const rows = errors.map((item) => {
    const itemStatus = statusOf(item);
    const [day, time] = dateText(item.date).split(" ");
    const phone = phoneOf(item);
    const open = expandedId === item._id;
    const toggle = () => setExpandedId(open ? null : item._id);
    return (
      <React.Fragment key={item._id}>
        <tr
          className="text-gray-700 cursor-pointer dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={(e) => {
            // The phone and the buttons do their own thing, and a drag selects text
            if (
              e.target.closest("a, button") ||
              window.getSelection().toString()
            ) {
              return;
            }
            toggle();
          }}
        >
          <td className="px-4 py-3 text-sm">
            <p>{day}</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">{time}</p>
          </td>
          <td className="px-4 py-3 text-sm">
            <p className="font-semibold">{item.printerName || item.printerId}</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {item.location || ""}
            </p>
          </td>
          {/* The one cell that wraps, so a long label does not widen the table */}
          <td className="px-4 py-3 text-sm whitespace-normal">
            <p style={{ minWidth: "10rem" }}>{errorCodeLabel(item.error_code)}</p>
          </td>
          <td className="px-4 py-3 text-sm font-semibold text-right">
            {/* A print paid from a page bundle has no money of its own */}
            {item.payment_type === "bundle" ? "Багц" : money(item.amount)}
          </td>
          <td className="px-4 py-3 text-sm">
            {phone ? (
              <a
                href={"tel:" + phone}
                className="font-semibold text-purple-600 dark:text-purple-400 hover:underline"
              >
                {phone}
              </a>
            ) : (
              <span className="text-gray-400 dark:text-gray-600">—</span>
            )}
          </td>
          <td className="px-4 py-3 text-sm">
            <span
              className={
                "inline-block px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
                itemStatus.badge
              }
            >
              {itemStatus.label}
            </span>
          </td>
          <td className="px-4 py-3 text-sm text-right">
            {item.status === "pending" ? (
              <button
                className="px-3 py-1 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
                onClick={() => openError(item)}
              >
                Шийдвэрлэх
              </button>
            ) : (
              ""
            )}
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 ml-2 text-gray-500 align-middle rounded-lg dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-200"
              aria-expanded={open}
              aria-label="Дэлгэрэнгүй"
              title="Дэлгэрэнгүй"
              onClick={toggle}
            >
              <svg
                className={
                  "w-4 h-4 transition-transform duration-150 transform" +
                  (open ? " rotate-180" : "")
                }
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          </td>
        </tr>
        {open && (
          <tr>
            <td
              colSpan={COLUMNS}
              className="px-4 py-4 whitespace-normal bg-gray-50 dark:bg-gray-900"
            >
              <PrintErrorDetail printError={item} />
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });

  return (
    <Admin>
      <>
        <h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">
          Хэвлэлтийн алдаа
        </h2>
        <form
          onSubmit={onSubmit}
          className="mb-8 bg-white rounded-lg shadow-md dark:bg-gray-800"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-200">
              <svg
                className="w-4 h-4 mr-2 text-purple-600 dark:text-purple-400"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Шүүлтүүр
            </div>
            {!loading && (
              <span className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-full dark:bg-gray-700 dark:text-purple-300">
                {errors.length} бичлэг
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 xl:grid-cols-4">
            <label className="block">
              <span className={labelClass}>Төлөв</span>
              <select
                name="status"
                value={status}
                onChange={(e) => onChange(e)}
                className={selectClass}
              >
                <option value="">Бүгд</option>
                {STATUS_FILTERS.map((key) => (
                  <option key={key} value={key}>
                    {STATUSES[key].label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={labelClass}>Киоск</span>
              <select
                name="printerId"
                value={printerId}
                onChange={(e) => onChange(e)}
                className={selectClass}
              >
                <option value="">Бүгд</option>
                {printers.map((printer) => (
                  <option key={printer._id} value={printer._id}>
                    {printer.printerName}
                    {printer.location ? " - " + printer.location : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="sm:col-span-2">
              <span className={labelClass}>Огноо</span>
              <div className="flex items-center">
                <input
                  type="date"
                  name="startDate"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(e) => onChange(e)}
                  aria-label="Эхлэх огноо"
                  className={dateClass}
                ></input>
                <span className="mx-2 text-gray-400 dark:text-gray-500">—</span>
                <input
                  type="date"
                  name="endDate"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(e) => onChange(e)}
                  aria-label="Дуусах огноо"
                  className={dateClass}
                ></input>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 px-4 py-3 border-t border-gray-100 rounded-b-lg sm:flex-row sm:items-center sm:justify-between bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
            <p className="flex items-center text-xs text-gray-500 dark:text-gray-400">
              <svg
                className="flex-shrink-0 w-4 h-4 mr-1"
                aria-hidden="true"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Хамгийн шинэ нь эхэндээ, дээд тал нь 500 бичлэг харагдана.
            </p>
            <div className="flex items-center justify-end gap-2">
              {canReset && (
                <button
                  type="button"
                  className={`px-4 py-2 text-sm font-medium leading-5 text-gray-700 transition-colors duration-150 bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                    loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={loading}
                  onClick={onReset}
                >
                  Цэвэрлэх
                </button>
              )}
              <button
                type="submit"
                className={`inline-flex items-center px-4 py-2 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <svg
                    className="w-4 h-4 mr-2 animate-spin"
                    aria-hidden="true"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 mr-2"
                    aria-hidden="true"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                )}
                Хайх
              </button>
            </div>
          </div>
        </form>

        {loading ? (
          <img
            src={spinner}
            className="my-6"
            style={{ width: "50px", margin: "auto", display: "block" }}
            alt="Loading..."
          />
        ) : errors.length === 0 ? (
          <p className="mb-8 text-sm text-gray-600 dark:text-gray-400">
            Бичлэг олдсонгүй.
          </p>
        ) : (
          <div className="w-full mb-8 overflow-hidden rounded-lg shadow-xs">
            <div className="w-full overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead>
                  <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                    <th className="px-4 py-3">Огноо</th>
                    <th className="px-4 py-3">Киоск</th>
                    <th className="px-4 py-3">Алдаа</th>
                    <th className="px-4 py-3 text-right">Төлбөр</th>
                    <th className="px-4 py-3">Холбоо барих</th>
                    <th className="px-4 py-3">Төлөв</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                  {rows}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && selected && selected.status === "pending" ? (
          <ResolvePrintError
            printError={selected}
            setShowModal={setShowModal}
            onConflict={() => getPrintErrors(loadedWith)}
          />
        ) : (
          ""
        )}
      </>
    </Admin>
  );
};

PrintErrors.propTypes = {
  getPrintErrors: PropTypes.func.isRequired,
  getPrinters: PropTypes.func.isRequired,
  printError: PropTypes.object.isRequired,
  printers: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  printError: state.printError,
  printers: state.printer.printers,
});

export default connect(mapStateToProps, { getPrintErrors, getPrinters })(
  PrintErrors
);
